import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Seo } from "@/components/common/Seo";
import { PageHero } from "@/components/common/PageHero";
import { CTAButton } from "@/components/common/CTAButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const input = "w-full bg-white border border-ink/20 rounded-md px-4 py-3 text-ink placeholder:text-ink/35 focus:outline-none focus:border-teal transition-colors";
const EMPTY_FORM = { title: "", tag: "Insights", read: "5 min", excerpt: "", cover_image: "", body: "", published: true };

const errMsg = (e, fallback) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).filter(Boolean).join(" ");
  return fallback;
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("adx_admin_token") || "");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(!!token);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [tab, setTab] = useState("articles");
  const [leads, setLeads] = useState([]);
  const [articles, setArticles] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null); // slug being edited
  const [uploading, setUploading] = useState(false);
  const [articleFilter, setArticleFilter] = useState("all");
  const [leadFilter, setLeadFilter] = useState("all");
  const [userForm, setUserForm] = useState({ email: "", name: "", password: "", role: "sales" });

  const isAdmin = user?.role === "admin";

  const authHeaders = (t = token) => ({ Authorization: `Bearer ${t}` });

  const signOut = useCallback(() => {
    localStorage.removeItem("adx_admin_token");
    setToken(""); setUser(null); setLeads([]); setArticles([]); setUsers([]);
  }, []);

  const load = useCallback(async (u = user, t = token) => {
    if (!u) return;
    const h = authHeaders(t);
    try {
      if (u.role === "admin") {
        const [l, a, us] = await Promise.all([
          axios.get(`${API}/admin/leads`, { headers: h }),
          axios.get(`${API}/admin/articles`, { headers: h }),
          axios.get(`${API}/auth/users`, { headers: h }),
        ]);
        setLeads(l.data || []); setArticles(a.data || []); setUsers(us.data || []);
      } else {
        const l = await axios.get(`${API}/admin/leads`, { headers: h });
        setLeads(l.data || []);
      }
    } catch (e) {
      if (e.response?.status === 401) { signOut(); toast.error("Session expired — sign in again"); }
    }
  }, [user, token, signOut]); // eslint-disable-line

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setUser(r.data); setTab(r.data.role === "admin" ? "articles" : "leads"); load(r.data, token); })
      .catch(() => signOut())
      .finally(() => setChecking(false));
  }, []); // eslint-disable-line

  const signIn = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.post(`${API}/auth/login`, loginForm);
      localStorage.setItem("adx_admin_token", r.data.token);
      setToken(r.data.token);
      setUser(r.data.user);
      setTab(r.data.user.role === "admin" ? "articles" : "leads");
      toast.success(`Welcome, ${r.data.user.name}`);
      load(r.data.user, r.data.token);
    } catch (err) { toast.error(errMsg(err, "Sign in failed")); }
  };

  const saveArticle = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/articles/${editing}`, form, { headers: authHeaders() });
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/articles`, form, { headers: authHeaders() });
        toast.success(form.published ? "Article published" : "Draft saved");
      }
      setForm(EMPTY_FORM); setEditing(null); load();
    } catch (err) { toast.error(errMsg(err, "Could not save article")); }
  };

  const startEdit = (a) => {
    setEditing(a.slug);
    setForm({ title: a.title, tag: a.tag, read: a.read, excerpt: a.excerpt, cover_image: a.cover_image || "", body: a.body, published: a.published });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const togglePublish = async (a) => {
    try {
      await axios.put(`${API}/articles/${a.slug}`, { published: !a.published }, { headers: authHeaders() });
      toast.success(a.published ? "Unpublished" : "Published");
      load();
    } catch (err) { toast.error(errMsg(err, "Update failed")); }
  };

  const remove = async (slug) => {
    try { await axios.delete(`${API}/articles/${slug}`, { headers: authHeaders() }); load(); }
    catch (err) { toast.error(errMsg(err, "Delete failed")); }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const r = await axios.post(`${API}/uploads`, data, { headers: authHeaders() });
      setForm((f) => ({ ...f, cover_image: r.data.url }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(errMsg(err, "Upload failed")); }
    finally { setUploading(false); }
  };

  const removeLead = async (id) => {
    try { await axios.delete(`${API}/leads/${id}`, { headers: authHeaders() }); toast.success("Lead deleted"); load(); }
    catch (err) { toast.error(errMsg(err, "Delete failed")); }
  };

  const exportLeadsCsv = () => {
    if (!leads.length) { toast.error("No leads to export"); return; }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = leads.map((l) => [
      l.lead_type, l.name, l.email, l.phone || "", l.organization || "", l.city || "",
      [l.message, ...Object.entries(l.details || {}).map(([k, v]) => v && `${k}: ${v}`)].filter(Boolean).join("; "),
      l.created_at || "",
    ].map(esc).join(","));
    const csv = ["type,name,email,phone,organization,city,details,submitted_at", ...rows].join("\n");
    const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `adx-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${leads.length} leads`);
  };

  const addUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/users`, userForm, { headers: authHeaders() });
      toast.success("Account created");
      setUserForm({ email: "", name: "", password: "", role: "sales" });
      load();
    } catch (err) { toast.error(errMsg(err, "Could not create account")); }
  };

  const removeUser = async (id) => {
    try { await axios.delete(`${API}/auth/users/${id}`, { headers: authHeaders() }); toast.success("Account removed"); load(); }
    catch (err) { toast.error(errMsg(err, "Could not remove account")); }
  };

  if (checking) return <div className="min-h-screen bg-paper" />;

  if (!user) {
    return (
      <>
        <Seo title="Admin — AdX" description="AdX admin" />
        <PageHero label="Admin" titleLines={["Team", "Access."]} accentIndex={1} />
        <section className="bg-paper py-24 md:py-32"><div className="container-px max-w-md">
          <form onSubmit={signIn} className="space-y-4">
            <div>
              <label className="block font-mono text-xs uppercase tracking-[0.18em] text-ink/50 mb-2">Email</label>
              <input type="email" required value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} data-testid="admin-email" className={input} placeholder="you@adx.vc" />
            </div>
            <div>
              <label className="block font-mono text-xs uppercase tracking-[0.18em] text-ink/50 mb-2">Password</label>
              <input type="password" required value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} data-testid="admin-password" className={input} placeholder="••••••••" />
            </div>
            <CTAButton type="submit" data-testid="admin-signin" arrow={false}>Sign in</CTAButton>
          </form>
        </div></section>
      </>
    );
  }

  return (
    <>
      <Seo title="Admin — AdX" description="AdX admin" />
      <PageHero label="Admin" titleLines={["Control", "Room."]} accentIndex={1} />
      <section className="bg-paper py-16 md:py-24"><div className="container-px">
        <div className="flex flex-wrap items-center gap-2 mb-10">
          {(isAdmin ? ["articles", "leads", "users"] : ["leads"]).map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
              className={`px-5 py-2.5 rounded-md font-display font-600 text-sm capitalize transition-colors ${tab === t ? "bg-ink text-white" : "bg-white border border-ink/15 text-ink/60 hover:border-ink/40"}`}>
              {t} {t === "leads" ? `(${leads.length})` : t === "articles" ? `(${articles.length})` : `(${users.length})`}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-4">
            <span data-testid="admin-user-badge" className="font-mono text-xs uppercase tracking-wider text-ink/45">{user.name} · {user.role}</span>
            <button onClick={signOut} data-testid="admin-signout" className="text-sm text-ink/60 hover:text-ink underline underline-offset-4">Sign out</button>
          </div>
        </div>

        {tab === "articles" && isAdmin && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <form onSubmit={saveArticle} data-testid="article-form" className="bg-white border border-ink/10 rounded-xl p-6 md:p-8 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-700 text-xl">{editing ? "Edit article" : "Publish an article"}</h3>
                {editing && (
                  <button type="button" onClick={() => { setEditing(null); setForm(EMPTY_FORM); }} data-testid="article-cancel-edit" className="text-sm text-ink/50 hover:text-ink underline underline-offset-4">Cancel edit</button>
                )}
              </div>
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" data-testid="article-title" className={input} />
              <div className="grid grid-cols-2 gap-4">
                <input value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} placeholder="Tag" data-testid="article-tag" className={input} />
                <input value={form.read} onChange={(e) => setForm({ ...form, read: e.target.value })} placeholder="Read time" data-testid="article-read" className={input} />
              </div>
              <div className="space-y-2">
                <label className="block font-mono text-xs uppercase tracking-[0.18em] text-ink/50">Cover image</label>
                <div className="flex gap-3">
                  <label data-testid="article-cover-upload"
                    className={`shrink-0 cursor-pointer font-display font-600 text-sm rounded-md px-5 py-3 border border-ink/20 bg-white transition-colors ${uploading ? "opacity-50 pointer-events-none" : "hover:border-ink/50"}`}>
                    {uploading ? "Uploading…" : "Upload image"}
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={uploadCover} className="hidden" data-testid="article-cover-file" />
                  </label>
                  <input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="…or paste an image URL (https://…)" data-testid="article-cover-image" className={input} />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-wider text-ink/35">JPEG, PNG, WebP or GIF · max 5 MB</p>
              </div>
              {form.cover_image && (
                <div className="relative">
                  <img src={form.cover_image} alt="Cover preview" data-testid="article-cover-preview" className="w-full h-40 object-cover rounded-lg border border-ink/10" onError={(e) => (e.currentTarget.style.display = "none")} onLoad={(e) => (e.currentTarget.style.display = "block")} />
                  <button type="button" onClick={() => setForm({ ...form, cover_image: "" })} data-testid="article-cover-clear" className="absolute top-2 right-2 bg-ink/70 text-white text-xs rounded-md px-2 py-1 hover:bg-ink">Remove</button>
                </div>
              )}
              <textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Excerpt" rows={2} data-testid="article-excerpt" className={input} />
              <textarea required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Body — plain paragraphs (blank line between) or HTML (<p>, <h2>, <ul><li>, <a>, <img>)" rows={8} data-testid="article-body-input" className={input} />
              <label className="flex items-center gap-3 text-sm text-ink/70 select-none">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="article-published-toggle" className="h-4 w-4 accent-[#FF5D5D]" />
                Visible on the public Insights page
              </label>
              <button type="submit" data-testid="article-save" className="font-display font-600 rounded-md px-6 py-3 bg-teal text-ink hover:bg-teal-bright transition-colors">
                {editing ? "Save changes" : form.published ? "Publish" : "Save draft"}
              </button>
            </form>
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="font-display font-700 text-xl">All articles ({articles.length})</h3>
                <div className="ml-auto flex gap-1.5" data-testid="article-filters">
                  {[["all", "All"], ["live", "Live"], ["draft", "Drafts"]].map(([v, label]) => {
                    const count = v === "all" ? articles.length : articles.filter((a) => (v === "live") === a.published).length;
                    return (
                      <button key={v} onClick={() => setArticleFilter(v)} data-testid={`article-filter-${v}`}
                        className={`px-3.5 py-1.5 rounded-md font-mono text-[10px] uppercase tracking-wider transition-colors ${articleFilter === v ? "bg-ink text-white" : "bg-white border border-ink/15 text-ink/55 hover:border-ink/40"}`}>
                        {label} ({count})
                      </button>
                    );
                  })}
                </div>
              </div>
              {articles.filter((a) => articleFilter === "all" || (articleFilter === "live") === a.published).map((a) => (
                <div key={a.slug} data-testid={`admin-article-${a.slug}`} className="flex items-center gap-3 bg-white border border-ink/10 rounded-lg p-4">
                  {a.cover_image && <img src={a.cover_image} alt="" className="h-12 w-16 object-cover rounded-md border border-ink/10 shrink-0" />}
                  <div className="min-w-0">
                    <p className="font-display font-600 truncate">{a.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-ink/40">
                      {a.tag} · /{a.slug} · <span className={a.published ? "text-teal" : "text-amber-600"}>{a.published ? "Live" : "Draft"}</span>
                    </p>
                  </div>
                  <div className="ml-auto flex items-center gap-3 shrink-0 text-sm">
                    <button onClick={() => togglePublish(a)} data-testid={`article-toggle-${a.slug}`} className="text-ink/60 hover:text-ink hover:underline">{a.published ? "Unpublish" : "Publish"}</button>
                    <button onClick={() => startEdit(a)} data-testid={`article-edit-${a.slug}`} className="text-ink/60 hover:text-ink hover:underline">Edit</button>
                    <button onClick={() => remove(a.slug)} data-testid={`article-delete-${a.slug}`} className="text-red-500 hover:underline">Delete</button>
                  </div>
                </div>
              ))}
              {!articles.filter((a) => articleFilter === "all" || (articleFilter === "live") === a.published).length && (
                <p className="text-ink/50" data-testid="articles-empty">{articles.length ? "No articles match this filter." : "No articles yet."}</p>
              )}
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="flex flex-wrap gap-1.5" data-testid="lead-filters">
                {[["all", "All"], ["society", "Societies"], ["commercial", "Commercial"], ["advertiser", "Advertisers"], ["contact", "Contact"]].map(([v, label]) => {
                  const count = v === "all" ? leads.length : leads.filter((l) => l.lead_type === v).length;
                  return (
                    <button key={v} onClick={() => setLeadFilter(v)} data-testid={`lead-filter-${v}`}
                      className={`px-3.5 py-1.5 rounded-md font-mono text-[10px] uppercase tracking-wider transition-colors ${leadFilter === v ? "bg-ink text-white" : "bg-white border border-ink/15 text-ink/55 hover:border-ink/40"}`}>
                      {label} ({count})
                    </button>
                  );
                })}
              </div>
              <button onClick={exportLeadsCsv} data-testid="leads-export-csv"
                className="ml-auto font-display font-600 text-sm rounded-md px-5 py-2.5 bg-white border border-ink/20 text-ink hover:border-ink/50 transition-colors">
                Download CSV
              </button>
            </div>
          <div data-testid="leads-inbox" className="overflow-x-auto rounded-xl border border-ink/10 bg-white">
            <table className="w-full text-sm min-w-[720px]">
              <thead><tr className="bg-ink text-white font-mono text-xs uppercase tracking-[0.14em] text-left">
                {["Type", "Name", "Email", "Phone", "Org", "City", "Details", "When", ...(isAdmin ? [""] : [])].map((h, i) => <th key={i} className="p-3">{h}</th>)}
              </tr></thead>
              <tbody>
                {leads.filter((l) => leadFilter === "all" || l.lead_type === leadFilter).map((l) => (
                  <tr key={l.id} className="border-t border-ink/10 align-top">
                    <td className="p-3"><span className="font-mono text-[10px] uppercase text-teal">{l.lead_type}</span></td>
                    <td className="p-3 font-600">{l.name}</td>
                    <td className="p-3">{l.email}</td>
                    <td className="p-3">{l.phone || "-"}</td>
                    <td className="p-3">{l.organization || "-"}</td>
                    <td className="p-3">{l.city || "-"}</td>
                    <td className="p-3 text-ink/60">{[l.message, ...Object.entries(l.details || {}).map(([k, v]) => v && `${k}: ${v}`)].filter(Boolean).join("; ") || "-"}</td>
                    <td className="p-3 text-ink/40 whitespace-nowrap">{(l.created_at || "").slice(0, 10)}</td>
                    {isAdmin && (
                      <td className="p-3">
                        <button onClick={() => removeLead(l.id)} data-testid={`lead-delete-${l.id}`} className="text-red-500 hover:underline whitespace-nowrap">Delete</button>
                      </td>
                    )}
                  </tr>
                ))}
                {!leads.filter((l) => leadFilter === "all" || l.lead_type === leadFilter).length && <tr><td colSpan={isAdmin ? 9 : 8} className="p-6 text-center text-ink/50">{leads.length ? "No leads in this category." : "No leads yet."}</td></tr>}
              </tbody>
            </table>
          </div>
          </div>
        )}

        {tab === "users" && isAdmin && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <form onSubmit={addUser} data-testid="user-form" className="bg-white border border-ink/10 rounded-xl p-6 md:p-8 space-y-4">
              <h3 className="font-display font-700 text-xl">Add a team account</h3>
              <input required value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} placeholder="Full name" data-testid="user-name" className={input} />
              <input required type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} placeholder="Email" data-testid="user-email" className={input} />
              <input required type="password" minLength={8} value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} placeholder="Password (min 8 characters)" data-testid="user-password" className={input} />
              <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })} data-testid="user-role" className={input}>
                <option value="sales">Sales — leads inbox only</option>
                <option value="admin">Admin — full access</option>
              </select>
              <button type="submit" data-testid="user-create" className="font-display font-600 rounded-md px-6 py-3 bg-teal text-ink hover:bg-teal-bright transition-colors">Create account</button>
            </form>
            <div className="space-y-3">
              <h3 className="font-display font-700 text-xl">Accounts ({users.length})</h3>
              {users.map((u) => (
                <div key={u.id} data-testid={`admin-user-${u.email}`} className="flex items-center gap-3 bg-white border border-ink/10 rounded-lg p-4">
                  <div className="min-w-0">
                    <p className="font-display font-600 truncate">{u.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-ink/40">{u.email} · {u.role}</p>
                  </div>
                  {u.id !== user.id && (
                    <button onClick={() => removeUser(u.id)} data-testid={`user-delete-${u.email}`} className="ml-auto text-sm text-red-500 hover:underline shrink-0">Remove</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div></section>
    </>
  );
}
